import React, { useEffect, useMemo, useState } from "react";
import {
    Badge,
    Button,
    Card,
    Container,
    Divider,
    Group,
    Loader,
    Modal,
    NumberInput,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    Text,
    TextInput,
    Textarea,
    Title,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { IconBuilding, IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";

import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billede1.jpg";
import { getEvents, type EventItem } from "../../../api/shiftsApi";
import {
    createHotel,
    createRoomType,
    deleteHotel,
    deleteRoomType,
    getHotelsForEvent,
    HotelDto,
    HotelRoomTypeDto,
    updateHotel,
    updateRoomType,
} from "../../../api/hotelBookingApi";

export default function HotelAdminPage() {
    const apiUrl = process.env.REACT_APP_API_URL as string;

    const [events, setEvents] = useState<EventItem[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null);

    const [hotels, setHotels] = useState<HotelDto[]>([]);
    const [loading, setLoading] = useState(false);

    const [hotelModalOpen, setHotelModalOpen] = useState(false);
    const [editingHotel, setEditingHotel] = useState<HotelDto | null>(null);

    const [roomModalOpen, setRoomModalOpen] = useState(false);
    const [selectedHotelForRoom, setSelectedHotelForRoom] = useState<HotelDto | null>(null);
    const [editingRoomType, setEditingRoomType] = useState<HotelRoomTypeDto | null>(null);

    const [saving, setSaving] = useState(false);

    const [hotelName, setHotelName] = useState("");
    const [hotelAddress, setHotelAddress] = useState("");
    const [hotelDescription, setHotelDescription] = useState("");
    const [bookingDeadline, setBookingDeadline] = useState<Date | null>(null);
    const [roomingDeadline, setRoomingDeadline] = useState<Date | null>(null);
    const [paymentDeadline, setPaymentDeadline] = useState<Date | null>(null);
    const [startBooking, setStartBooking] = useState<Date | null>(null);
    const [endBooking, setEndBooking] = useState<Date | null>(null);

    const [hotelActive, setHotelActive] = useState(true);

    const [roomName, setRoomName] = useState("");
    const [roomCapacity, setRoomCapacity] = useState<number | "">(1);
    const [roomPrice, setRoomPrice] = useState<number | "">(0);
    const [roomAvailable, setRoomAvailable] = useState<number | "">(0);
    const [roomActive, setRoomActive] = useState(true);

    const eventSelectData = useMemo(
        () =>
            events.map((e) => ({
                value: String(e.eventID),
                label: e.eventName || `Event ${e.eventID}`,
            })),
        [events]
    );

    useEffect(() => {
        async function loadEvents() {
            try {
                const data = await getEvents(apiUrl);
                setEvents(data);
            } catch (e) {
                console.error(e);
                notifications.show({
                    color: "red",
                    title: "Fejl",
                    message: "Kunne ikke hente events",
                });
            }
        }

        loadEvents();
    }, [apiUrl]);

    useEffect(() => {
        if (!selectedEventId) {
            setHotels([]);
            return;
        }

        loadHotels();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEventId]);

    async function loadHotels() {
        if (!selectedEventId) return;

        setLoading(true);
        try {
            const data = await getHotelsForEvent(apiUrl, selectedEventId);
            setHotels(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            notifications.show({
                color: "red",
                title: "Fejl",
                message: "Kunne ikke hente hoteller",
            });
            setHotels([]);
        } finally {
            setLoading(false);
        }
    }

    function toIsoOrNull(date: Date | null) {
        return date ? date.toISOString() : null;
    }

    function resetHotelForm() {
        setEditingHotel(null);
        setHotelName("");
        setHotelAddress("");
        setHotelDescription("");
        setBookingDeadline(null);
        setPaymentDeadline(null);
        setStartBooking(null);
        setEndBooking(null);
        setRoomingDeadline(null);
        setHotelActive(true);
    }

    function openCreateHotel() {
        if (!selectedEventId) {
            notifications.show({
                color: "yellow",
                title: "Vælg event",
                message: "Du skal vælge et event først",
            });
            return;
        }

        resetHotelForm();
        setHotelModalOpen(true);
    }

    function openEditHotel(hotel: HotelDto) {
        setEditingHotel(hotel);
        setHotelName(hotel.name ?? "");
        setHotelAddress(hotel.address ?? "");
        setHotelDescription(hotel.description ?? "");
        setBookingDeadline(hotel.bookingDeadline ? new Date(hotel.bookingDeadline) : null);
        setPaymentDeadline(
            hotel.paymentDeadline
                ? new Date(hotel.paymentDeadline)
                : null
        );

        setStartBooking(
            hotel.startBooking
                ? new Date(hotel.startBooking)
                : null
        );

        setEndBooking(
            hotel.endBooking
                ? new Date(hotel.endBooking)
                : null
        );
        setRoomingDeadline(hotel.roomingDeadline ? new Date(hotel.roomingDeadline) : null);
        setHotelActive(hotel.isActive);
        setHotelModalOpen(true);
    }

    async function saveHotel() {
        if (!selectedEventId) return;

        if (!hotelName.trim()) {
            notifications.show({
                color: "red",
                title: "Mangler navn",
                message: "Hotelnavn skal udfyldes",
            });
            return;
        }

        setSaving(true);

        try {
            const dto = {
                eventID: selectedEventId,
                name: hotelName.trim(),
                address: hotelAddress || null,
                description: hotelDescription || null,
                bookingDeadline: toIsoOrNull(bookingDeadline),
                paymentDeadline: toIsoOrNull(paymentDeadline),
                roomingDeadline: toIsoOrNull(roomingDeadline),

                startBooking: toIsoOrNull(startBooking),
                endBooking: toIsoOrNull(endBooking),

                isActive: hotelActive,
            };

            if (editingHotel) {
                await updateHotel(apiUrl, editingHotel.hotelID, dto);
            } else {
                await createHotel(apiUrl, dto);
            }

            notifications.show({
                color: "green",
                title: "Gemt",
                message: editingHotel ? "Hotel blev opdateret" : "Hotel blev oprettet",
            });

            setHotelModalOpen(false);
            resetHotelForm();
            await loadHotels();
        } catch (e: any) {
            notifications.show({
                color: "red",
                title: "Fejl",
                message: e?.message || "Kunne ikke gemme hotel",
            });
        } finally {
            setSaving(false);
        }
    }

    async function onDeleteHotel(hotel: HotelDto) {
        if (!window.confirm(`Deaktivér ${hotel.name}?`)) return;

        try {
            await deleteHotel(apiUrl, hotel.hotelID);
            notifications.show({
                color: "green",
                title: "Deaktiveret",
                message: "Hotel blev deaktiveret",
            });
            await loadHotels();
        } catch (e: any) {
            notifications.show({
                color: "red",
                title: "Fejl",
                message: e?.message || "Kunne ikke deaktivere hotel",
            });
        }
    }

    function resetRoomForm() {
        setEditingRoomType(null);
        setRoomName("");
        setRoomCapacity(1);
        setRoomPrice(0);
        setRoomAvailable(0);
        setRoomActive(true);
    }

    function openCreateRoomType(hotel: HotelDto) {
        setSelectedHotelForRoom(hotel);
        resetRoomForm();
        setRoomModalOpen(true);
    }

    function openEditRoomType(hotel: HotelDto, room: HotelRoomTypeDto) {
        setSelectedHotelForRoom(hotel);
        setEditingRoomType(room);
        setRoomName(room.name ?? "");
        setRoomCapacity(room.capacity ?? 1);
        setRoomPrice(room.pricePerNight ?? 0);
        setRoomAvailable(room.availableRooms ?? 0);
        setRoomActive(room.isActive);
        setRoomModalOpen(true);
    }

    async function saveRoomType() {
        if (!selectedHotelForRoom) return;

        if (!roomName.trim()) {
            notifications.show({
                color: "red",
                title: "Mangler navn",
                message: "Room type navn skal udfyldes",
            });
            return;
        }

        setSaving(true);

        try {
            const dto = {
                name: roomName.trim(),
                capacity: Number(roomCapacity || 1),
                pricePerNight: Number(roomPrice || 0),
                availableRooms: Number(roomAvailable || 0),
                isActive: roomActive,
            };

            if (editingRoomType) {
                await updateRoomType(apiUrl, editingRoomType.hotelRoomTypeID, dto);
            } else {
                await createRoomType(apiUrl, selectedHotelForRoom.hotelID, dto);
            }

            notifications.show({
                color: "green",
                title: "Gemt",
                message: editingRoomType
                    ? "Room type blev opdateret"
                    : "Room type blev oprettet",
            });

            setRoomModalOpen(false);
            resetRoomForm();
            await loadHotels();
        } catch (e: any) {
            notifications.show({
                color: "red",
                title: "Fejl",
                message: e?.message || "Kunne ikke gemme room type",
            });
        } finally {
            setSaving(false);
        }
    }

    async function onDeleteRoomType(room: HotelRoomTypeDto) {
        if (!window.confirm(`Deaktivér ${room.name}?`)) return;

        try {
            await deleteRoomType(apiUrl, room.hotelRoomTypeID);
            notifications.show({
                color: "green",
                title: "Deaktiveret",
                message: "Room type blev deaktiveret",
            });
            await loadHotels();
        } catch (e: any) {
            notifications.show({
                color: "red",
                title: "Fejl",
                message: e?.message || "Kunne ikke deaktivere room type",
            });
        }
    }

    function formatDate(value?: string | null) {
        if (!value) return "-";
        return new Date(value).toLocaleString("da-DK");
    }

    function formatMoney(value: number) {
        return new Intl.NumberFormat("da-DK", {
            style: "currency",
            currency: "DKK",
        }).format(value || 0);
    }

    return (
        <div>
            <HeroSectionV2
                titleEn="Hotel admin"
                titleDa="Hotel administration"
                descriptionEn=""
                descriptionDa=""
                imageSrc={Billede1}
                badgeText="Hotels"
            />

            <Container size="xl" py="md">
                <Stack gap="md">
                    <Paper withBorder p="lg" radius="md">
                        <Group justify="space-between" align="flex-end">
                            <Stack gap={2}>
                                <Title order={3}>Hotel setup</Title>
                                <Text c="dimmed" size="sm">
                                    Opret hoteller, deadlines, værelsestyper og priser pr. nat.
                                </Text>
                            </Stack>

                            <Group align="flex-end">
                                <Select
                                    label="Vælg event"
                                    placeholder="Vælg event..."
                                    data={eventSelectData}
                                    value={selectedEventId ? String(selectedEventId) : null}
                                    onChange={(v) => setSelectedEventId(v ? Number(v) : null)}
                                    searchable
                                    w={320}
                                />

                                <Button leftSection={<IconPlus size={16} />} onClick={openCreateHotel}>
                                    Opret hotel
                                </Button>
                            </Group>
                        </Group>
                    </Paper>

                    {!selectedEventId ? (
                        <Paper withBorder p="xl" radius="md">
                            <Text c="dimmed">Vælg et event for at administrere hoteller.</Text>
                        </Paper>
                    ) : loading ? (
                        <Paper withBorder p="xl" radius="md">
                            <Group justify="center">
                                <Loader />
                            </Group>
                        </Paper>
                    ) : hotels.length === 0 ? (
                        <Paper withBorder p="xl" radius="md">
                            <Text c="dimmed">Der er ikke oprettet hoteller endnu.</Text>
                        </Paper>
                    ) : (
                        hotels.map((hotel) => (
                            <Card key={hotel.hotelID} withBorder radius="md" p="lg">
                                <Stack gap="md">
                                    <Group justify="space-between" align="flex-start">
                                        <Group align="flex-start">
                                            <IconBuilding size={28} />
                                            <Stack gap={2}>
                                                <Group>
                                                    <Title order={4}>{hotel.name}</Title>
                                                    <Badge color={hotel.isActive ? "green" : "gray"}>
                                                        {hotel.isActive ? "Active" : "Inactive"}
                                                    </Badge>
                                                </Group>

                                                <Text size="sm" c="dimmed">
                                                    {hotel.address || "Ingen adresse"}
                                                </Text>

                                                {hotel.description && (
                                                    <Text size="sm">{hotel.description}</Text>
                                                )}
                                            </Stack>
                                        </Group>

                                        <Group>
                                            <Button
                                                variant="light"
                                                leftSection={<IconEdit size={16} />}
                                                onClick={() => openEditHotel(hotel)}
                                            >
                                                Redigér
                                            </Button>

                                            <Button
                                                color="red"
                                                variant="light"
                                                leftSection={<IconTrash size={16} />}
                                                onClick={() => onDeleteHotel(hotel)}
                                            >
                                                Deaktivér
                                            </Button>
                                        </Group>
                                    </Group>

                                    <Paper withBorder p="sm" radius="md" bg="gray.0">
                                        <Group grow>
                                            <Stack gap={2}>
                                                <Text size="xs" c="dimmed">
                                                    Booking deadline
                                                </Text>
                                                <Text size="sm" fw={600}>
                                                    {formatDate(hotel.bookingDeadline)}
                                                </Text>
                                            </Stack>

                                            <Stack gap={2}>
                                                <Text size="xs" c="dimmed">
                                                    Betalingsfrist
                                                </Text>
                                                <Text size="sm" fw={600}>
                                                    {formatDate(hotel.paymentDeadline)}
                                                </Text>
                                            </Stack>
                                            <Stack gap={2}>
                                                <Text size="xs" c="dimmed">
                                                    Booking åbner
                                                </Text>
                                                <Text size="sm" fw={600}>
                                                    {formatDate(hotel.startBooking)}
                                                </Text>
                                            </Stack>

                                            <Stack gap={2}>
                                                <Text size="xs" c="dimmed">
                                                    Booking lukker
                                                </Text>
                                                <Text size="sm" fw={600}>
                                                    {formatDate(hotel.endBooking)}
                                                </Text>
                                            </Stack>

                                            <Stack gap={2}>
                                                <Text size="xs" c="dimmed">
                                                    Rooming deadline
                                                </Text>
                                                <Text size="sm" fw={600}>
                                                    {formatDate(hotel.roomingDeadline)}
                                                </Text>
                                            </Stack>
                                        </Group>
                                    </Paper>

                                    <Divider />

                                    <Group justify="space-between">
                                        <Title order={5}>Room types</Title>
                                        <Button
                                            size="xs"
                                            variant="light"
                                            leftSection={<IconPlus size={14} />}
                                            onClick={() => openCreateRoomType(hotel)}
                                        >
                                            Tilføj room type
                                        </Button>
                                    </Group>

                                    {hotel.roomTypes?.length ? (
                                        <Table striped highlightOnHover withTableBorder>
                                            <Table.Thead>
                                                <Table.Tr>
                                                    <Table.Th>Navn</Table.Th>
                                                    <Table.Th>Kapacitet</Table.Th>
                                                    <Table.Th>Pris / nat</Table.Th>
                                                    <Table.Th>Antal værelser</Table.Th>
                                                    <Table.Th>Status</Table.Th>
                                                    <Table.Th style={{ width: 180 }}>Handling</Table.Th>
                                                </Table.Tr>
                                            </Table.Thead>
                                            <Table.Tbody>
                                                {hotel.roomTypes.map((room) => (
                                                    <Table.Tr key={room.hotelRoomTypeID}>
                                                        <Table.Td>{room.name}</Table.Td>
                                                        <Table.Td>{room.capacity}</Table.Td>
                                                        <Table.Td>{formatMoney(room.pricePerNight)}</Table.Td>
                                                        <Table.Td>{room.availableRooms}</Table.Td>
                                                        <Table.Td>
                                                            <Badge color={room.isActive ? "green" : "gray"}>
                                                                {room.isActive ? "Active" : "Inactive"}
                                                            </Badge>
                                                        </Table.Td>
                                                        <Table.Td>
                                                            <Group gap="xs">
                                                                <Button
                                                                    size="xs"
                                                                    variant="light"
                                                                    onClick={() => openEditRoomType(hotel, room)}
                                                                >
                                                                    Redigér
                                                                </Button>
                                                                <Button
                                                                    size="xs"
                                                                    color="red"
                                                                    variant="light"
                                                                    onClick={() => onDeleteRoomType(room)}
                                                                >
                                                                    Deaktivér
                                                                </Button>
                                                            </Group>
                                                        </Table.Td>
                                                    </Table.Tr>
                                                ))}
                                            </Table.Tbody>
                                        </Table>
                                    ) : (
                                        <Text c="dimmed" size="sm">
                                            Ingen room types oprettet endnu.
                                        </Text>
                                    )}
                                </Stack>
                            </Card>
                        ))
                    )}
                </Stack>
            </Container>

            <Modal
                opened={hotelModalOpen}
                onClose={() => setHotelModalOpen(false)}
                title={editingHotel ? "Redigér hotel" : "Opret hotel"}
                size="lg"
            >
                <Stack>
                    <TextInput
                        label="Hotelnavn"
                        value={hotelName}
                        onChange={(e) => setHotelName(e.currentTarget.value)}
                        required
                    />

                    <TextInput
                        label="Adresse"
                        value={hotelAddress}
                        onChange={(e) => setHotelAddress(e.currentTarget.value)}
                    />

                    <Textarea
                        label="Beskrivelse"
                        value={hotelDescription}
                        onChange={(e) => setHotelDescription(e.currentTarget.value)}
                        minRows={3}
                    />

                    <Group grow wrap="wrap">
                        <DateTimePicker
                            label="Booking deadline"
                            value={bookingDeadline}
                            onChange={setBookingDeadline}
                            clearable
                        />

                        <DateTimePicker
                            label="Betalingsfrist"
                            value={paymentDeadline}
                            onChange={setPaymentDeadline}
                            clearable
                        />
                    </Group>
                    <Group grow>
                        <DateTimePicker
                            label="Booking åbner"
                            value={startBooking}
                            onChange={setStartBooking}
                            clearable
                        />

                        <DateTimePicker
                            label="Booking lukker"
                            value={endBooking}
                            onChange={setEndBooking}
                            clearable
                        />
                    </Group>

                    <DateTimePicker
                        label="Rooming deadline"
                        value={roomingDeadline}
                        onChange={setRoomingDeadline}
                        clearable
                    />

                    <Switch
                        label="Aktiv"
                        checked={hotelActive}
                        onChange={(e) => setHotelActive(e.currentTarget.checked)}
                    />

                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setHotelModalOpen(false)}>
                            Annuller
                        </Button>
                        <Button loading={saving} onClick={saveHotel}>
                            Gem
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            <Modal
                opened={roomModalOpen}
                onClose={() => setRoomModalOpen(false)}
                title={
                    editingRoomType
                        ? "Redigér room type"
                        : `Tilføj room type${selectedHotelForRoom ? ` · ${selectedHotelForRoom.name}` : ""}`
                }
                size="md"
            >
                <Stack>
                    <TextInput
                        label="Navn"
                        placeholder="Single / Double / Triple"
                        value={roomName}
                        onChange={(e) => setRoomName(e.currentTarget.value)}
                        required
                    />

                    <Group grow>
                        <NumberInput
                            label="Kapacitet"
                            value={roomCapacity}
                            onChange={(v) =>
                                setRoomCapacity(typeof v === "number" ? v : v ? Number(v) : "")
                            }
                            min={1}
                            required
                        />

                        <NumberInput
                            label="Antal værelser"
                            value={roomAvailable}
                            onChange={(v) =>
                                setRoomAvailable(typeof v === "number" ? v : v ? Number(v) : "")
                            }
                            min={0}
                            required
                        />
                    </Group>

                    <NumberInput
                        label="Pris pr. nat"
                        value={roomPrice}
                        onChange={(v) =>
                            setRoomPrice(typeof v === "number" ? v : v ? Number(v) : "")
                        }
                        min={0}
                        decimalScale={2}
                        required
                    />

                    <Switch
                        label="Aktiv"
                        checked={roomActive}
                        onChange={(e) => setRoomActive(e.currentTarget.checked)}
                    />

                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setRoomModalOpen(false)}>
                            Annuller
                        </Button>
                        <Button loading={saving} onClick={saveRoomType}>
                            Gem
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </div>
    );
}